import React, { useEffect, useState } from 'react';
import {
    Box, Checkbox, FormControlLabel, FormGroup, Typography,
} from '@mui/material';
import http from '../../services/http';

/**
 * Quais MARCAS o operador atende — SysFar, MAX.
 *
 * ## O pedido
 *
 * Gisele (19/10/2026): "configuração do operador: informar se atende MaxSmart".
 * Esclarecido pelo dono do produto em 20/10: "hoje um usuário do suporte pode
 * atender SysFar e Max, e tem operadores que irão atender somente Max ou só
 * SysFar ou ambos. Mas não se priva somente ao suporte. E seria para conseguir
 * diferenciar essas filas."
 *
 * ## Lista, e não uma caixinha "atende MaxSmart"
 *
 * Decisão do dono do produto na mesma conversa. A marca não é constante do
 * código: é linha em `whatsapp_numbers`, e já são duas no mesmo tenant. Uma
 * caixinha por marca exigiria código novo na terceira — e um número novo
 * cadastrado não apareceria sozinho aqui.
 *
 * ## "Nenhuma marcada" é ATENDE TODAS
 *
 * E está escrito na tela, porque é o contrário do que a caixa vazia sugere.
 * Sem o aviso, um administrador abriria o cadastro de alguém que atende tudo,
 * veria tudo desmarcado e concluiria que a configuração se perdeu. É também o
 * estado de todos os operadores hoje.
 *
 * ## É do operador, não do departamento
 *
 * "Não se priva somente ao suporte": a mesma pessoa pode ser de qualquer
 * departamento e atender uma marca só. Por isso o controle vive no cadastro do
 * operador, ao lado do departamento, e não dentro dele.
 */

/**
 * As marcas do tenant, buscadas UMA vez por sessão.
 *
 * Mesma razão do cache de `useFasesDoRelacionamento`: este controle aparece em
 * três formulários (a página de novo operador, o modal de criar e o de
 * editar), e sem o cache seriam três requisições para a mesma lista de duas
 * linhas, que praticamente nunca muda.
 */
let promessaDasMarcas = null;

export const useMarcasDisponiveis = () => {
    const [marcas, setMarcas] = useState([]);

    useEffect(() => {
        let cancelado = false;

        if (!promessaDasMarcas) {
            promessaDasMarcas = http.get('/whatsapp-numbers/marcas')
                .then(({ data }) => (Array.isArray(data?.data) ? data.data : []))
                // Falha NÃO fica em cache: um erro de rede momentâneo não deve
                // deixar a lista vazia pelo resto da sessão.
                .catch(() => { promessaDasMarcas = null; return []; });
        }

        promessaDasMarcas.then((lista) => {
            if (!cancelado) setMarcas(lista);
        });

        return () => { cancelado = true; };
    }, []);

    return marcas;
};

/**
 * Os ids que o formulário deve mandar, a partir do que a api devolveu.
 *
 * A lista de operadores entrega `whatsapp_number_ids` (quando o payload é
 * montado) ou a relação `marcas_que_atende` (quando é o model serializado).
 * As duas formas chegam a esta tela, e normalizar aqui evita cada formulário
 * descobrir a sua — foi o que já aconteceu com a marca da sala
 * (`utils/marcaDaSala`).
 */
export const idsDasMarcasDoOperador = (operador) => {
    if (Array.isArray(operador?.whatsapp_number_ids)) {
        return operador.whatsapp_number_ids.map(Number);
    }

    if (Array.isArray(operador?.marcas_que_atende)) {
        return operador.marcas_que_atende.map((m) => Number(m.id));
    }

    return [];
};

const MarcasQueAtende = ({ marcas, selecionadas, aoMudar, disabled = false }) => {
    const lista = Array.isArray(marcas) ? marcas : [];
    const marcadas = Array.isArray(selecionadas) ? selecionadas.map(Number) : [];

    // Tenant sem número de WhatsApp cadastrado não tem marca para escolher, e
    // um bloco vazio só ocuparia o formulário.
    if (lista.length === 0) return null;

    const alternar = (id) => {
        const numero = Number(id);
        const nova = marcadas.includes(numero)
            ? marcadas.filter((m) => m !== numero)
            : [...marcadas, numero];

        aoMudar(nova);
    };

    return (
        <Box sx={{ mt: 1 }} data-testid="marcas-que-atende">
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
                Marcas que atende
            </Typography>
            <FormGroup row>
                {lista.map((m) => (
                    <FormControlLabel
                        key={m.id}
                        control={
                            <Checkbox
                                size="small"
                                checked={marcadas.includes(Number(m.id))}
                                onChange={() => alternar(m.id)}
                                disabled={disabled}
                                inputProps={{ 'data-testid': `marca-${m.id}` }}
                            />
                        }
                        label={<Typography sx={{ fontSize: '13px' }}>{m.rotulo || m.display_number}</Typography>}
                    />
                ))}
            </FormGroup>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {marcadas.length === 0
                    ? 'Nenhuma marcada: o operador atende a fila inteira.'
                    : 'A fila dele mostra só as marcas marcadas (e as conversas sem marca, como o chat do site).'}
            </Typography>
        </Box>
    );
};

export default MarcasQueAtende;
